import React, { useEffect, useMemo, useState } from 'react';
import {
  DesignServices,
  FileDownload,
  ReadMore,
  Visibility,
  ArrowCircleDown,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { BatchUploadDialog } from './BatchUploadDialog';
import { visaStatusOptions } from './option';
import { ARFilter } from '../ARFilter';
import { AR_LIST_TEXT, QUALIFICATION_PAGE_TEXT } from '../../lang';
import {
  type ARVisaApplicationData,
  useGetARVisaApplicationList,
} from '../../graphql';
import { getOptionsLabel } from '../../../Monitor/components';
import { programmeTypeOptions } from '../../../Application/components';
import {
  Alert,
  type AlertDetails,
  Button,
  ButtonContainer,
  type HeadCell,
  IconButton,
  Loading,
  Table,
} from '../../../../components';
import {
  handleViewAsApplicant,
  postRequest,
  type PostRequestData,
  getRequest,
  type GetRequestData,
} from '../../../../utils';
import { VisaTypeUpdate } from './VisaTypeUpdate';

export type VisaApplicationProps = {
  filter?: any;
};

export const ARVisaApplication: React.FunctionComponent<
  VisaApplicationProps
> = () => {
  const [visibleData, setVisibleData] = useState<ARVisaApplicationData[]>([]);
  const [batchUploadDialogOpen, setBatchUploadDialogOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const [filter, setFilter] = useState<any>(null);
  const [appliedFilter, setAppliedFilter] = useState<any>(null);
  const [checkedRows, setCheckedRows] = useState<number[]>([]); // to store all index of checked row(s)
  const [checkedAll, setCheckedAll] = useState<boolean>(false);

  const memoizedFilter = useMemo(() => {
    return { filter, appliedFilter };
  }, [filter, appliedFilter]);

  useEffect(() => {
    setFilter(memoizedFilter.filter);
    setAppliedFilter(memoizedFilter.appliedFilter);
  }, [memoizedFilter]);

  const {
    arVisaApplications,
    departmentsAndEntryYear,
    countryList: nationalityOptions,
    loading: arVisaApplicationsLoading,
    error: arVisaApplicationsError,
    refetch,
  } = useGetARVisaApplicationList(filter ?? {});

  const loading = arVisaApplicationsLoading;

  const visaTemplateUrl = process.env.REACT_APP_AR_VISA_INFO_CSV_TEMPLATE_FILE;

  const dataCells: Array<HeadCell<ARVisaApplicationData>> = [
    {
      id: 'applicationNo',
      label: AR_LIST_TEXT.APPLICATION_NO,
      isSortable: false,
      render: (row: ARVisaApplicationData) => row.applicationNo,
    },
    {
      id: 'applicantName',
      label: AR_LIST_TEXT.APPLICANT_NAME,
      isSortable: false,
      render: (row: ARVisaApplicationData) => row.applicantName,
    },
    {
      id: 'applicantEmail',
      label: AR_LIST_TEXT.APPLICANT_EMAIL,
      isSortable: false,
      render: (row: ARVisaApplicationData) => row.applicantEmail,
    },
    {
      id: 'programmeType',
      label: AR_LIST_TEXT.PROG_TYPE,
      isSortable: false,
      render: (row: ARVisaApplicationData) =>
        getOptionsLabel(programmeTypeOptions, row.programmeType),
    },
    {
      id: 'programmeCode',
      label: AR_LIST_TEXT.PROG_CODE,
      isSortable: false,
      render: (row: ARVisaApplicationData) => row.programmeCode,
    },
    {
      id: 'entryYear',
      label: AR_LIST_TEXT.ENTRY_YEAR,
      isSortable: false,
      render: (row: ARVisaApplicationData) => row.entryYear,
    },
    {
      id: 'applicantNationality',
      label: AR_LIST_TEXT.NATIONALITY,
      isSortable: false,
      render: (row: ARVisaApplicationData) =>
        getOptionsLabel(nationalityOptions, row.applicantNationality),
    },
    {
      id: 'status',
      label: AR_LIST_TEXT.VISA_STATUS,
      isSortable: false,
      render: (row: ARVisaApplicationData) =>
        getOptionsLabel(visaStatusOptions, row.status),
    },
    {
      id: 'referenceNo',
      label: AR_LIST_TEXT.VISA_REF,
      isSortable: false,
      render: (row: ARVisaApplicationData) => row.referenceNo,
    },
    {
      id: 'action',
      label: AR_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: ARVisaApplicationData) => action(row),
    },
  ];

  const action = (row: ARVisaApplicationData): React.ReactNode => {
    const { applicationId } = row;
    return (
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-2">
          <IconButton
            onClick={handleViewDetail(applicationId)}
            color="primary"
            icon={<ReadMore />}
          />

          <IconButton
            onClick={handleViewMonitor(applicationId)}
            color="primary"
            icon={<Visibility />}
          />

          <IconButton
            onClick={handleViewAsApplicant(applicationId, status)}
            title="View as Applicant"
            color="primary"
            icon={<DesignServices />}
          />
        </div>
      </div>
    );
  };

  const handleChangeCheckedIndex = (checkedIndex: number[]): void => {
    setCheckedRows(checkedIndex);
  };

  const handleChangeCheckAll = (checkAll: boolean): void => {
    setCheckedAll(checkAll);
  };

  const handleViewDetail =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) {
        window.open(`/internal/ar-homepage/ar-process/${applicationId}?am=2`);
      }
    };

  const handleViewMonitor =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) window.open(`/internal/monitor/${applicationId}`);
    };

  const handleClickOpen = (): void => {
    setBatchUploadDialogOpen(true);
  };

  const handleDownloadCSVTemplate = async (): Promise<void> => {
    try {
      const getRequestData: GetRequestData = {
        url: `/files/${visaTemplateUrl}`,
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      };
      const response = await getRequest(getRequestData);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AR_VISA_TEMPLATE.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleOpenErrorAlert(error);
    }
  };

  const handleBatchUploadDialogClose = (): void => {
    setBatchUploadDialogOpen(false);
  };

  useEffect(() => {
    if (!arVisaApplicationsLoading && !arVisaApplicationsError) {
      const { data } = arVisaApplications;
      setVisibleData(data);
    }
  }, [arVisaApplicationsLoading, arVisaApplications]);

  const handleOpenErrorAlert = (error: any): void => {
    setAlertDetails({
      alertContent: 'Error Occurs.' + (error as Error)?.message,
      alertType: 'error',
    });
    setOpenAlert(true);
  };

  if (arVisaApplicationsError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (arVisaApplicationsError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleBatchExportVisaInfo = async (): Promise<void> => {
    if (disableExport) return;

    const exportApplicationIds: string[] = checkedRows.map(rowIndex => {
      const { applicationId } = visibleData[rowIndex];
      return applicationId;
    });

    try {
      const postData: PostRequestData = {
        url: `/eadmin-operation/ar/export/visa`,
        data: JSON.stringify({ applicationIds: exportApplicationIds }),
        responseType: 'blob',
      };

      const response = await postRequest(postData);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const formattedDateTime = dayjs().format('DD/MM/YYYY_HH-mm-ss');
      link.setAttribute('download', `visa-${formattedDateTime}.xlsx`);

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url);
    } catch (error) {
      handleOpenErrorAlert(error);
    }
  };

  const disableExport = !checkedRows.length || !visibleData.length; // no application has been selected

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <>
        <ARFilter
          showVisa
          initialAppliedFilter={memoizedFilter.appliedFilter}
          departmentsAndEntryYear={departmentsAndEntryYear}
          setFilter={(filter: any, appliedFilter: any) => {
            setFilter(filter);
            setAppliedFilter(appliedFilter);
          }}
        />
        {loading ? (
          <Loading />
        ) : (
          <>
            <VisaTypeUpdate refetchList={refetch} />

            <ButtonContainer spacing={1}>
              <Button
                onClick={handleClickOpen}
                variant="contained"
                className="!normal-case"
              >
                {QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT.BATCH_UPLOAD}
              </Button>
              <Button
                onClick={handleDownloadCSVTemplate}
                disabled={!visaTemplateUrl}
                startIcon={<ArrowCircleDown />}
                variant="text"
                className="!normal-case"
              >
                {QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT.CSV_TEMPLATE}
              </Button>
            </ButtonContainer>

            <ButtonContainer paddingY={'0.5rem'} spacing={1}>
              <Button
                onClick={handleBatchExportVisaInfo}
                disabled={disableExport}
                className="!normal-case"
                startIcon={<FileDownload />}
              >
                {QUALIFICATION_PAGE_TEXT.EXPORT}
              </Button>
            </ButtonContainer>

            <Table<ARVisaApplicationData>
              showCheckBox
              checkBoxProps={{
                checkedIndex: checkedRows,
                onChangeCheckedIndex: handleChangeCheckedIndex,
                checkedAll,
                onCheckAll: handleChangeCheckAll,
              }}
              headCells={dataCells}
              data={visibleData}
              handleRequestSort={() => {}}
            />
          </>
        )}
      </>

      <BatchUploadDialog
        open={batchUploadDialogOpen}
        handleClose={handleBatchUploadDialogClose}
        refetchList={refetch}
      />
    </>
  );
};
